import { create } from 'zustand';
import { ReactNode } from 'react';

interface ErrorInfo {
  id: string;
  title?: string;
  message: string;
  code?: string;
  type?: 'dialog' | 'popover';
  trigger?: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface ErrorStore {
  errors: ErrorInfo[];
  addError: (error: Omit<ErrorInfo, 'id'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
  showErrorDialog: (title: string | undefined, message: string, code?: string) => string;
  showErrorPopover: (trigger: ReactNode, title: string | undefined, message: string, code?: string, position?: 'top' | 'bottom' | 'left' | 'right') => string;
  showPopover: (message: string, trigger?: ReactNode, position?: 'top' | 'bottom' | 'left' | 'right') => string;
}

export const useErrorStore = create<ErrorStore>((set, get) => ({
  errors: [],  // 🚨 完全重置错误列表
  
  addError: (error) => {
    // 检查是否已经存在相同的错误消息，防止弹窗叠加
    const currentErrors = get().errors;
    if (currentErrors.some(e => e.message === error.message)) {
      return '';
    }

    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newError: ErrorInfo = { id, ...error };
    
    set((state) => ({
      errors: [...state.errors, newError],
    }));
    
    // 自动移除弹窗错误（10秒后）
    if (error.type !== 'popover') {
      setTimeout(() => {
        get().removeError(id);
      }, 10000);
    }
    
    return id;
  },
  
  showErrorDialog: (title, message, code) => {
    return get().addError({
      title,
      message,
      code,
      type: 'dialog'
    });
  },
  
  // 提供清理方法但平时不使用
  clearAllErrors: () => {
    console.log('Error store cleared');
    set({ errors: [] });
  },

  removeError: (id) => {
    set((state) => ({
      errors: state.errors.filter((error) => error.id !== id),
    }));
  },

  clearErrors: () => {
    set({ errors: [] });
  },

  showErrorPopover: (trigger, title, message, code, position) => {
    return get().addError({
      title,
      message,
      code,
      type: 'popover',
      trigger,
      position,
    }) || '';
  },

  showPopover: (message, trigger, position) => {
    return get().addError({
      title: '操作成功',
      message,
      type: 'popover',
      trigger,
      position,
    }) || '';
  },
}));

// Helper functions for common error scenarios
export const showError = (message: string, title?: string, code?: string) => {
  return useErrorStore.getState().showErrorDialog(title, message, code);
};

export const showSuccess = (message: string, trigger?: ReactNode, position?: 'top' | 'bottom' | 'left' | 'right') => {
  return useErrorStore.getState().showPopover(
    message,
    trigger,
    position
  );
};

export const showNetworkError = (error: Error) => {
  return useErrorStore.getState().showErrorDialog(
    '网络错误',
    error.message || '网络请求失败，请检查网络连接',
    'NETWORK_ERROR'
  );
};

export const showApiError = (message: string, code?: string) => {
  return useErrorStore.getState().showErrorDialog(
    'API 错误',
    message,
    code
  );
};